/**
 * AuthContext - Multi-Tenant Authentication Context
 * 
 * Provides authentication state and user information including:
 * - User authentication status
 * - User role (super_admin, company_admin, company_user)
 * - Company information
 * - Custom claims
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getCustomClaims, refreshCustomClaims } from '../utils/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [company, setCompany] = useState(null);
  const [role, setRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load user data from Firestore
   */
  const loadUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error loading user data:', error);
      return null;
    }
  };

  /**
   * Load company data from Firestore
   */
  const loadCompanyData = async (companyId) => {
    if (!companyId) {
      setCompany(null);
      return null;
    }

    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const data = { id: companyDoc.id, ...companyDoc.data() };
        setCompany(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error loading company data:', error);
      return null;
    }
  };

  /**
   * Load custom claims and related data
   */
  const loadCustomClaims = async (user) => {
    try {
      console.log('🔍 [AuthContext] Cargando custom claims para:', user.email);
      
      // Intentar obtener claims con múltiples refrescos si es necesario
      let claims = await getCustomClaims();
      console.log('🔍 [AuthContext] Custom claims obtenidos (intento 1):', claims);
      
      // 🔥 HOTFIX: Si no hay role O si es company_admin/user sin companyId, forzar hasta 3 refrescos
      let attempts = 0;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts && claims && (!claims.role || (claims.role !== 'super_admin' && !claims.companyId))) {
        attempts++;
        console.log(`🔄 [AuthContext] Claims incompletos (intento ${attempts}/${maxAttempts}), forzando refresco del token...`);
        
        await user.getIdToken(true); // Force refresh
        await new Promise(resolve => setTimeout(resolve, 300 * attempts)); // Espera progresiva
        
        claims = await getCustomClaims();
        console.log(`🔍 [AuthContext] Custom claims después de refresco ${attempts}:`, claims);
        
        // Si ya tenemos role y (es super_admin O tiene companyId), salir del loop
        if (claims && claims.role && (claims.role === 'super_admin' || claims.companyId)) {
          console.log('✅ [AuthContext] Claims completos obtenidos');
          break;
        }
      }
      
      if (claims) {
        setRole(claims.role || null);
        setCompanyId(claims.companyId || null);
        
        console.log('🔍 [AuthContext] Role establecido:', claims.role);
        console.log('🔍 [AuthContext] CompanyId establecido:', claims.companyId);
        
        // Load company data if user has a company
        if (claims.companyId) {
          const companyData = await loadCompanyData(claims.companyId);
          console.log('🔍 [AuthContext] Datos de empresa cargados:', companyData?.name);
        } else {
          setCompany(null);
          console.log('🔍 [AuthContext] Sin empresa asignada');
        }
      } else {
        console.log('⚠️ [AuthContext] No se encontraron custom claims después de todos los intentos');
        setRole(null);
        setCompanyId(null);
        setCompany(null);
      }
    } catch (error) {
      console.error('❌ [AuthContext] Error loading custom claims:', error);
      setRole(null);
      setCompanyId(null);
      setCompany(null);
    } finally {
      setClaimsLoaded(true);
    }
  };

  /**
   * Sign in with email and password
   */
  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // 🔥 CRÍTICO: Forzar refresh del token para obtener custom claims actualizados
      console.log('🔄 [AuthContext] Forzando refresh de token después de login...');
      await result.user.getIdToken(true);
      
      // Esperar un momento para que los claims se propaguen
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Load user data and custom claims
      await loadUserData(result.user.uid);
      await loadCustomClaims(result.user);
      
      console.log('✅ [AuthContext] Login completado con custom claims cargados');
      
      return result.user;
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign in with SSO Token from People app
   */
  const signInWithSSO = async (ssoToken) => {
    try {
      setError(null);
      console.log('🔐 Llamando a validateSSOToken...');
      
      // Llamar al backend para validar y obtener custom token
      const VALIDATE_SSO_URL = import.meta.env.VITE_VALIDATE_SSO_URL || 
        'https://us-central1-firma-digital-db89b.cloudfunctions.net/validateSSOToken';
      
      const response = await fetch(VALIDATE_SSO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssoToken })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Token SSO inválido');
      }

      const { customToken, email, uid, role, companyId } = await response.json();
      console.log('✅ Custom token recibido:', { email, uid, role, companyId });
      
      // Importar signInWithCustomToken
      const { signInWithCustomToken } = await import('firebase/auth');
      
      // Iniciar sesión con el custom token
      const result = await signInWithCustomToken(auth, customToken);
      console.log('✅ signInWithCustomToken exitoso:', result.user.email);
      
      // CRÍTICO: Forzar múltiples refrescos para asegurar que los claims se propaguen
      console.log('🔄 Forzando recarga de claims...');
      await result.user.getIdToken(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Esperar propagación
      
      // Verificar claims finales
      const tokenResult = await result.user.getIdTokenResult(true);
      console.log('🔍 Claims verificados:', {
        role: tokenResult.claims.role,
        companyId: tokenResult.claims.companyId,
        uid: result.user.uid,
        email: result.user.email
      });
      
      // Si los claims están vacíos aún, usar los del response
      if (!tokenResult.claims.role && role) {
        console.warn('⚠️ Claims no propagados, usando valores del backend');
        setRole(role);
        setCompanyId(companyId || null);
        
        if (companyId) {
          await loadCompanyData(companyId);
        }
      } else {
        // Claims correctos, cargar normalmente
        await loadCustomClaims(result.user);
      }
      
      // Load user data
      await loadUserData(result.user.uid);
      
      return result.user;
    } catch (error) {
      console.error('❌ Error en login SSO:', error);
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      console.log('🚪 Cerrando sesión...');
      
      // Limpiar datos de localStorage
      localStorage.removeItem('currentUserId');
      localStorage.removeItem('userEmail');
      
      await firebaseSignOut(auth);
      
      setUser(null);
      setUserData(null);
      setCompany(null);
      setRole(null);
      setCompanyId(null);
      setError(null);
      
      console.log('✅ Sesión cerrada correctamente');
    } catch (error) {
      console.error('❌ Error cerrando sesión:', error);
      setError(error.message);
      throw error;
    }
  };

  /**
   * Refresh user data and custom claims
   */
  const refresh = async () => {
    if (!auth.currentUser) return;
    
    try {
      // Refresh custom claims
      await refreshCustomClaims();
      
      // Reload user data
      await loadUserData(auth.currentUser.uid);
      await loadCustomClaims(auth.currentUser);
    } catch (error) {
      console.error('Refresh error:', error);
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (requiredRole) => {
    if (!role) return false;
    
    const roleHierarchy = {
      'super_admin': 3,
      'company_admin': 2,
      'company_user': 1
    };
    
    const userLevel = roleHierarchy[role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  };

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = () => role === 'super_admin';

  /**
   * Check if user is company admin
   */
  const isCompanyAdmin = () => role === 'company_admin';

  /**
   * Check if user is company user
   */
  const isCompanyUser = () => role === 'company_user';

  /**
   * Check if user belongs to specific company
   */
  const belongsToCompany = (checkCompanyId) => {
    return companyId === checkCompanyId;
  };

  /**
   * Verify session consistency
   */
  const verifySession = () => {
    const currentUserId = localStorage.getItem('currentUserId');
    const currentUserEmail = localStorage.getItem('userEmail');
    
    if (user && currentUserId && user.uid !== currentUserId) {
      console.warn('⚠️ Inconsistencia de sesión detectada:', {
        authUser: user.uid,
        storedUser: currentUserId,
        authEmail: user.email,
        storedEmail: currentUserEmail
      });
      return false;
    }
    
    return true;
  };

  // Listen to auth state changes
  useEffect(() => {
    // Limpiar cualquier sesión persistida en localStorage de versiones anteriores
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('firebase:authUser:')) localStorage.removeItem(key);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 Auth state changed:', user ? `${user.email} (${user.uid})` : 'null');
      
      setLoading(true);
      
      if (user) {
        // Verificar que el usuario no haya cambiado inesperadamente
        const currentUserId = localStorage.getItem('currentUserId');
        const currentUserEmail = localStorage.getItem('userEmail');
        
        if (currentUserId && currentUserId !== user.uid) {
          console.warn('⚠️ Usuario cambió inesperadamente:', { 
            previous: currentUserId, 
            current: user.uid,
            previousEmail: currentUserEmail,
            currentEmail: user.email
          });
          
          // Limpiar datos inconsistentes
          localStorage.removeItem('currentUserId');
          localStorage.removeItem('userEmail');
        }
        
        // Guardar ID del usuario actual
        localStorage.setItem('currentUserId', user.uid);
        localStorage.setItem('userEmail', user.email || '');
        
        setUser(user);
        
        // Load user data and custom claims with retry logic
        let retries = 3;
        while (retries > 0) {
          try {
            await loadUserData(user.uid);
            await loadCustomClaims(user);
            break; // Success, exit retry loop
          } catch (error) {
            console.error(`❌ Error loading user data (${4 - retries}/3):`, error);
            retries--;
            if (retries === 0) {
              console.error('❌ Failed to load user data after 3 attempts');
              setError('Error cargando datos del usuario. Por favor, recarga la página.');
            } else {
              // Wait before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
      } else {
        console.log('🚪 Usuario desconectado');
        
        // Limpiar datos de sesión
        localStorage.removeItem('currentUserId');
        localStorage.removeItem('userEmail');
        
        setUser(null);
        setUserData(null);
        setCompany(null);
        setRole(null);
        setCompanyId(null);
        setError(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // 🔄 Renovar token automáticamente cada 45 minutos para evitar expiración silenciosa
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      try {
        await user.getIdToken(true);
        console.log('🔄 Token renovado automáticamente');
      } catch (error) {
        console.error('❌ Error renovando token:', error);
        // Si no se puede renovar, cerrar sesión con aviso
        if (error.code === 'auth/user-token-expired' || 
            error.code === 'auth/user-disabled' ||
            error.code === 'auth/invalid-user-token') {
          console.warn('⚠️ Sesión expirada, cerrando sesión...');
          await firebaseSignOut(auth);
        }
      }
    };

    // Renovar cada 45 minutos
    const interval = setInterval(refreshToken, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // 🔐 SSO: Detectar token SSO en la URL al cargar la app
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('ssoToken');
    
    if (ssoToken) {
      console.log('🔐 Token SSO detectado en URL');
      
      // Limpiar URL inmediatamente para evitar reusos
      window.history.replaceState({}, '', window.location.pathname);
      
      // Procesar SSO con manejo de usuario existente
      const doSSO = async () => {
        try {
          const currentUser = auth.currentUser;
          
          // Si hay usuario (real o anónimo), hacer logout primero
          if (currentUser) {
            const isAnonymous = currentUser.isAnonymous;
            console.log('⚠️ Usuario ya logueado:', currentUser.email || '(anónimo)');
            
            if (isAnonymous) {
              console.log('🔄 Usuario anónimo detectado, haciendo logout...');
            } else {
              console.log('🔄 Forzando logout para SSO...');
            }
            
            // Forzar logout (funciona para usuarios normales y anónimos)
            await firebaseSignOut(auth);
            
            // Esperar a que el auth state se actualice
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          
          // Proceder con SSO
          console.log('🚀 Iniciando proceso SSO...');
          await signInWithSSO(ssoToken);
          console.log('✅ SSO completado exitosamente');
          
        } catch (error) {
          console.error('❌ Error en proceso SSO:', error);
        }
      };
      
      doSSO();
    }
  }, []); // Solo ejecutar una vez al montar

  const value = {
    // State
    user,
    userData,
    company,
    role,
    companyId,
    loading,
    claimsLoaded,
    error,
    
    // Methods
    signIn,
    signInWithSSO,
    signOut,
    refresh,
    
    // Helpers
    hasRole,
    isSuperAdmin,
    isCompanyAdmin,
    isCompanyUser,
    belongsToCompany,
    verifySession,
    
    // Computed
    isAuthenticated: !!user,
    hasCompany: !!company,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  
  return context;
}

export default AuthContext;
